/**
 * SF-4940: batch actions on the Companies list. Set one account type on many companies, and
 * schedule many Demo or Test companies for deletion, one request per company. Account type uses
 * the same PATCH as the company page. Deletion does NOT use the danger zone's DELETE: it uses
 * sf-api's schedule-only route, which checks the type in the claim and does not exist on older
 * servers (whose DELETE purges at once).
 *
 * SF-5158: switch many companies on or off with the Active switch's PATCH; only inactive
 * companies can be scheduled for deletion.
 */
import type { DataProvider } from '@refinedev/core';
import { runBatch, type BatchFailure, type BatchResult } from '../../utils/batch';
import { COMPANIES_API_BASE, type CompanyDeleteImpact } from './api/companyDeleteClient';
import { canEditCompanyId } from './api/editableCompaniesClient';
import {
  ACCOUNT_TYPES,
  ACCOUNT_TYPE_LABELS,
  COMPANIES_META,
  INTERNAL_ACCOUNT_TYPE,
  type AccountType,
} from './constants';
import type { Company } from './list';

/** Only these account types can be deleted in a batch (Alan, SF-4940). */
export const BATCH_DELETABLE_TYPES: readonly AccountType[] = ['Demo', 'Test'];

/**
 * Types a batch can set. Not ServeFirst Live (SF-5100): only one company may hold it, so it is
 * set on that company's own page.
 */
export const BATCH_SETTABLE_TYPES: readonly AccountType[] = ACCOUNT_TYPES.filter(
  (t) => t !== INTERNAL_ACCOUNT_TYPE,
);

export const companyId = (c: Company): string => c._id || c.id || '';

/**
 * Why a company can't be ticked, or null when it can. Paid companies are never in a batch;
 * a company already on its way out is handled from Deleted companies; and staff limited to
 * some companies can only tick those (`editScope` is the settled sf-api answer, null = unknown).
 */
export function batchTickBlock(company: Company, editScope: unknown): string | null {
  if (company.deletion_status === 'deleting') return 'Being deleted';
  if (company.deletion_status) return 'Waiting for deletion';
  // No stored type reads as Paid on the server (the schema default).
  if (!company.account_type || company.account_type === 'Paid') {
    return 'Paid companies are changed one at a time';
  }
  // SF-5100: ServeFirst's own working company is never in a batch, so a batch can't turn it into
  // Demo or Test and then delete it.
  if (company.account_type === INTERNAL_ACCOUNT_TYPE) {
    return `${ACCOUNT_TYPE_LABELS[INTERNAL_ACCOUNT_TYPE]} is changed on its own page`;
  }
  if (!canEditCompanyId(editScope, companyId(company))) return 'Not on your list of companies';
  return null;
}

/** True when every company may be scheduled for deletion together. */
export const allBatchDeletable = (companies: Company[]): boolean =>
  companies.length > 0 &&
  companies.every((c) => !!c.account_type && BATCH_DELETABLE_TYPES.includes(c.account_type));

/**
 * sf-api answers `{ success: false, message }`. A 5xx never shows the server's own text, which
 * may be a raw exception (same rule as the danger zone). With no reply at all, the error's own
 * text is shown (a network error, or one of this file's own refusals).
 */
export function companyBatchErrorMessage(err: unknown): string {
  const e = err as {
    response?: { status?: number; data?: { message?: unknown } };
    message?: unknown;
  };
  const status = e?.response?.status;
  if (status !== undefined) {
    const serverMessage = e.response?.data?.message;
    if (status >= 500) return 'Something went wrong. Please try again.';
    if (status === 403) return 'You are not allowed to change this company.';
    // An sf-api from before batch deletion has no schedule route (Express's own 404, no message).
    if (status === 404 && !(typeof serverMessage === 'string' && serverMessage.trim())) {
      return 'This server cannot schedule deletions in a batch yet. Use the company page.';
    }
    return typeof serverMessage === 'string' && serverMessage.trim()
      ? serverMessage
      : `Request failed (${status})`;
  }
  return typeof e?.message === 'string' && e.message.trim() ? e.message : 'Unknown error';
}

type Provider = DataProvider;

/**
 * The server copy of a company, refused (thrown) when a batch must not touch it any more:
 * it became Paid, or it is on its way out.
 */
async function readBatchable(provider: Provider, id: string): Promise<Company> {
  const { data: company } = await provider.getOne<Company>({
    resource: 'companies',
    id,
    meta: COMPANIES_META,
  });
  if (!company) throw new Error('Company not found');
  if (company.deletion_status) throw new Error('Already waiting for deletion');
  if (!company.account_type || company.account_type === 'Paid') {
    throw new Error('It is Paid now, so it can only be changed on its own page');
  }
  if (company.account_type === INTERNAL_ACCOUNT_TYPE) {
    throw new Error(
      `It is ${ACCOUNT_TYPE_LABELS[INTERNAL_ACCOUNT_TYPE]} now, so it can only be changed on its own page`,
    );
  }
  return company;
}

/** SF-5158: why a batch did not schedule a company that is still active. */
const STILL_ACTIVE_ERROR = 'Still active: mark it inactive first';

/** sf-api's schedule-only deletion route (SF-4940), used by the batch. */
export const scheduleDeletionUrl = (id: string) => `/${COMPANIES_API_BASE}/${id}/deletion/schedule`;

/** The company's delete-impact answer (counts, waiting period, open deletion). */
export async function fetchDeleteImpact(
  provider: Provider,
  id: string,
): Promise<CompanyDeleteImpact | undefined> {
  const { data } = await provider.custom!<CompanyDeleteImpact>({
    url: `/${COMPANIES_API_BASE}/${id}/delete-impact`,
    method: 'get',
  });
  return data;
}

/** True when this sf-api schedules deletions (a waiting period of at least a day). */
export const schedulesDeletion = (
  impact: CompanyDeleteImpact | undefined,
): impact is CompanyDeleteImpact & { graceDays: number } =>
  typeof impact?.graceDays === 'number' && impact.graceDays >= 1;

/** A company batch's outcome; `skipped` are companies left alone on purpose, with the reason. */
export interface CompanyBatchResult extends BatchResult {
  skipped: BatchFailure[];
}

/** SF-5106: sf-api's answer when the company's type is no longer the one the batch read. */
export const ACCOUNT_TYPE_CHANGED = 'ACCOUNT_TYPE_CHANGED';

/** The skip reason when sf-api answered 409 ACCOUNT_TYPE_CHANGED, else null. */
export function accountTypeChangedReason(err: unknown): string | null {
  const response = (err as { response?: { status?: number; data?: unknown } })?.response;
  const data = response?.data as
    | { code?: unknown; current?: { account_type?: unknown } }
    | undefined;
  if (response?.status !== 409 || data?.code !== ACCOUNT_TYPE_CHANGED) return null;
  const now = data.current?.account_type;
  const label =
    typeof now === 'string' ? (ACCOUNT_TYPE_LABELS[now as AccountType] ?? now) : 'no account type';
  return `Skipped: it was changed to ${label} after the batch read it`;
}

/**
 * Sets `accountType` on every company in `ids`. SF-5106: each save names the type the batch read
 * (`expected_account_type`), so sf-api saves only while the company still has it; a company
 * changed in between (made Paid or ServeFirst Live, say) is skipped, not overwritten.
 */
export const setAccountTypeForCompanies = async (
  provider: Provider,
  ids: string[],
  accountType: AccountType,
): Promise<CompanyBatchResult> => {
  const skipped = new Map<string, string>();
  const result = await runBatch(
    ids,
    async (id) => {
      // The tick may be old (kept across pages): check the server copy is still allowed.
      const company = await readBatchable(provider, id);
      try {
        await provider.update({
          resource: 'companies',
          id,
          variables: { account_type: accountType, expected_account_type: company.account_type },
          meta: COMPANIES_META,
        });
      } catch (err) {
        const reason = accountTypeChangedReason(err);
        if (!reason) throw err;
        skipped.set(id, reason);
        return 'unchanged';
      }
      return 'updated';
    },
    undefined,
    companyBatchErrorMessage,
  );
  return {
    ...result,
    unchanged: result.unchanged.filter((id) => !skipped.has(id)),
    skipped: [...skipped].map(([id, message]) => ({ id, message })),
  };
};

/** SF-5158: active unless explicitly inactive (a company stored without it is active). */
export const isCompanyActive = (c: Pick<Company, 'is_active'>): boolean => c.is_active !== false;

/**
 * SF-5158: switches every company in `ids` on or off, through the same one-field PATCH as the
 * company page's Active switch. A company already in that state is left as it was and listed as
 * skipped ("Already inactive" / "Already active").
 */
export const setActiveForCompanies = async (
  provider: Provider,
  ids: string[],
  isActive: boolean,
): Promise<CompanyBatchResult> => {
  const skipped = new Map<string, string>();
  const result = await runBatch(
    ids,
    async (id) => {
      const company = await readBatchable(provider, id);
      if (isCompanyActive(company) === isActive) {
        skipped.set(id, `Already ${isActive ? 'active' : 'inactive'}`);
        return 'unchanged';
      }
      await provider.update({
        resource: 'companies',
        id,
        variables: { is_active: isActive },
        meta: COMPANIES_META,
      });
      return 'updated';
    },
    undefined,
    companyBatchErrorMessage,
  );
  return {
    ...result,
    unchanged: result.unchanged.filter((id) => !skipped.has(id)),
    skipped: [...skipped].map(([id, message]) => ({ id, message })),
  };
};

/**
 * Schedules the deletion of every company in `ids`, each checked again on the server copy
 * first: still Demo or Test, inactive (SF-5158), not already on its way out, and the server
 * SCHEDULES deletions (its delete-impact answer has a waiting period). An sf-api without
 * scheduled deletion deletes at once, so a company is never sent there from a batch. The request goes to the
 * schedule-only route and names the allowed types, so sf-api refuses one whose type changed
 * after these reads.
 */
export const scheduleDeletionForCompanies = async (
  provider: Provider,
  ids: string[],
): Promise<CompanyBatchResult> => ({
  skipped: [],
  ...(await runBatch(
    ids,
    async (id) => {
      const company = await readBatchable(provider, id);
      if (!company.account_type || !BATCH_DELETABLE_TYPES.includes(company.account_type)) {
        throw new Error('Only Demo and Test companies can be deleted together');
      }
      if (isCompanyActive(company)) throw new Error(STILL_ACTIVE_ERROR);
      const impact = await fetchDeleteImpact(provider, id);
      if (impact?.isActive === true) throw new Error(STILL_ACTIVE_ERROR);
      if (!schedulesDeletion(impact)) {
        throw new Error('This server deletes at once, so it can only be deleted from its own page');
      }
      if (impact.deletionStatus || impact.deletion) {
        throw new Error('Already waiting for deletion');
      }
      // The schedule-only route, never DELETE: a server from before scheduled deletion has no
      // such route (404), where its DELETE would purge at once. `accountTypes` makes sf-api check
      // the type again in the write that schedules it, so a company made Paid a moment ago is
      // refused (409), not deleted.
      await provider.custom!({
        url: scheduleDeletionUrl(id),
        method: 'post',
        payload: {
          confirmName: impact.companyName ?? company.name,
          accountTypes: [...BATCH_DELETABLE_TYPES],
        },
      });
      return 'updated';
    },
    undefined,
    companyBatchErrorMessage,
  )),
});
